import React, { useEffect, useState } from "react";
import { Table, Button, Form, Row, Col, Card, Modal } from "react-bootstrap";
import axios from "axios";
import API_BASE_URL from "../config";

const ShiftView = ({ employeeId }) => {
  const [templates, setTemplates] = useState([]);
  const [employeeShifts, setEmployeeShifts] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result.toISOString().split("T")[0];
  };

  // Then use:
  const [toDate, setToDate] = useState(addDays(new Date(), 30));
  const [showSubstituteModal, setShowSubstituteModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [substituteForm, setSubstituteForm] = useState({
    substitute_employee_id: "",
  });
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    start_time: "",
    end_time: "",
    description: "",
  });
  const [templateErrors, setTemplateErrors] = useState({});
  useEffect(() => {
    fetchCompanyId();
  }, [employeeId]);

  const fetchCompanyId = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/employees/${employeeId}`);
      setCompanyId(res.data.company_id);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchTemplates();
      fetchEmployeeShifts();
    }
  }, [companyId, fromDate, toDate]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/shift_templates`
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setTemplates(data);
    } catch (err) {
      console.error("Failed to load shift templates:", err);
      setTemplates([]); // Always array
    }
  };

  const fetchEmployeeShifts = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/shifts`,
        {
          params: { from: fromDate, to: toDate },
        }
      );
      setEmployeeShifts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAutoAssign = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/employees/${employeeId}/auto_assign_shifts`,
        {
          from_date: fromDate,
          to_date: toDate,
          shift_template_id:
            document.getElementById("template-select")?.value || null,
        }
      );
      fetchEmployeeShifts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubstitute = (assignment) => {
    setSelectedAssignment(assignment);
    setSubstituteForm({ substitute_employee_id: "" });
    fetchAvailableEmployees();
    setShowSubstituteModal(true);
  };

  const fetchAvailableEmployees = async () => {
    // Fetch employees with availability (simple query)
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/employees`
      );
      setAvailableEmployees(
        res.data.filter((emp) => emp.id !== employeeId && !emp.shifts_on_date)
      ); // Pseudo-filter
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubstituteSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_BASE_URL}/shift_assignments/${selectedAssignment.id}/substitute`,
        substituteForm
      );
      setShowSubstituteModal(false);
      fetchEmployeeShifts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubstituteChange = (e) => {
    setSubstituteForm({ substitute_employee_id: e.target.value });
  };
  const handleCreateTemplate = async () => {
    const errors = {};
    if (!templateForm.name) errors.name = "Name is required";
    if (!templateForm.start_time) errors.start_time = "Start time is required";
    if (!templateForm.end_time) errors.end_time = "End time is required";
    if (Object.keys(errors).length > 0) {
      setTemplateErrors(errors);
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/companies/${companyId}/shift_templates`,
        templateForm
      );
      setShowTemplateModal(false);
      setTemplateForm({
        name: "",
        start_time: "",
        end_time: "",
        description: "",
      });
      setTemplateErrors({});
      fetchTemplates(); // Refresh list
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create template";
      setTemplateErrors({ name: msg });
    }
  };
  return (
    <div>
      <h4>Shift & Scheduling Management</h4>

      {/* Shift Templates */}
      <h5>Shift Templates</h5>
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Select id="template-select">
                <option>Select Template</option>
                {(Array.isArray(templates) ? templates : []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.start_time} - {t.end_time})
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
             <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowTemplateModal(true)}
              >
                + Add Template
              </Button>&nbsp;
              <Button onClick={handleAutoAssign}>Auto-Assign Shifts</Button>
            </Col>
          </Row>
          
        </Card.Body>
      </Card>

      {/* Shift Calendar */}
      <h5>Shift Calendar</h5>
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Col>
        <Col md={6}>
          <Button onClick={fetchEmployeeShifts}>Refresh Calendar</Button>
        </Col>
      </Row>
      <Table striped hover>
        <thead>
          <tr>
            <th>Date</th>
            <th>Shift</th>
            <th>Start - End</th>
            <th>Status</th>
            <th>Substitute</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {employeeShifts.map((shift) => (
            <tr key={shift.id}>
              <td>{shift.assigned_date}</td>
              <td>{shift.name}</td>
              <td>
                {shift.start_time} - {shift.end_time}
              </td>
              <td>{shift.status}</td>
              <td>{shift.substitute_name || "—"}</td>
              <td>
                {shift.status === "assigned" && (
                  <Button size="sm" onClick={() => handleSubstitute(shift)}>
                    Assign Substitute
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Substitute Modal */}
      <Modal
        show={showSubstituteModal}
        onHide={() => setShowSubstituteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Assign Substitute for {selectedAssignment?.assigned_date}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubstituteSubmit}>
            <Form.Group>
              <Form.Label>Select Substitute</Form.Label>
              <Form.Select
                value={substituteForm.substitute_employee_id}
                onChange={handleSubstituteChange}
                required
              >
                <option>Select Employee</option>
                {availableEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Button type="submit" className="mt-3">
              Assign
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
      {/* Add Template Modal */}
      <Modal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Shift Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={templateForm.name}
                onChange={(e) =>
                  setTemplateForm({ ...templateForm, name: e.target.value })
                }
                isInvalid={!!templateErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {templateErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={templateForm.start_time}
                    onChange={(e) =>
                      setTemplateForm({
                        ...templateForm,
                        start_time: e.target.value,
                      })
                    }
                    isInvalid={!!templateErrors.start_time}
                  />
                  <Form.Control.Feedback type="invalid">
                    {templateErrors.start_time}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={templateForm.end_time}
                    onChange={(e) =>
                      setTemplateForm({
                        ...templateForm,
                        end_time: e.target.value,
                      })
                    }
                    isInvalid={!!templateErrors.end_time}
                  />
                  <Form.Control.Feedback type="invalid">
                    {templateErrors.end_time}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={templateForm.description}
                onChange={(e) =>
                  setTemplateForm({
                    ...templateForm,
                    description: e.target.value,
                  })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowTemplateModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateTemplate}>
            Create Template
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ShiftView;
